import { ContentType, MessagePart, processRenderMarkers, StructuredOutput } from './util';

export type StreamState = {
  structuredOutputsCache: Map<string, StructuredOutput>;
  currentContent: string;
  thinkingContent: string;
  statusContent: string;
  currentRoundId: string | null;
  rounds: ReasoningRound[];
  replyFinished: boolean;
};

export type ReasoningRound = {
  messageId: string;
  textContent: string;
  thinkingContent: string;
  statusContent: string;
  toolStatuses: string[];
  thinkingTime?: number; // 每轮思考独立的时间（秒）
};

export type ReasoningContent = {
  text: string;
  thinking: string;
  toolStatus: string;
  hasToolUse: boolean;
  hasToolUseBlock: boolean;
};

export type ReasoningEventState = {
  isLast: boolean;
  isFinalAnswer: boolean;
};

export type ThinkingTimelineRound = {
  id: string;
  title: string;
  content: string;
  toolStatuses: string[];
  thinkingTime?: number;
  active: boolean;
};

export const createStreamState = (options?: { statusContent?: string; replyFinished?: boolean }): StreamState => ({
  structuredOutputsCache: new Map<string, StructuredOutput>(),
  currentContent: '',
  thinkingContent: '',
  statusContent: options?.statusContent ?? '',
  currentRoundId: null,
  rounds: [],
  replyFinished: options?.replyFinished ?? false,
});

export const mergeReasoningContent = (existing: string, incoming: string) => {
  if (!incoming) return existing;
  if (!existing) return incoming;
  if (incoming.startsWith(existing)) return incoming;
  if (existing.endsWith(incoming)) return existing;
  return existing + incoming;
};

export const parseMaybeJson = (value: any) => {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

export const getEventPayload = (event: any) => parseMaybeJson(event?.eventData ?? event?.data ?? event);

export const getEventType = (event: any, fallback?: string) =>
  String(event?.type ?? event?.eventType ?? getEventPayload(event)?.type ?? fallback ?? '').toUpperCase();

export const getEventLast = (event: any) =>
  Boolean(event?.isLast ?? event?.last ?? getEventPayload(event)?.isLast ?? getEventPayload(event)?.last);

const getMessage = (event: any) => getEventPayload(event)?.message ?? event?.message;

export const getEventMessageId = (event: any, fallback?: string) => {
  const payload = getEventPayload(event);
  const message = getMessage(event);
  return String(event?.messageId ?? payload?.messageId ?? message?.id ?? fallback ?? '');
};

export const getMessageContent = (event: any) => {
  const content = getMessage(event)?.content;
  return Array.isArray(content) ? content : [];
};

const getBlockType = (block: any) => String(block?.type ?? '').toLowerCase();

export const getTextFromBlock = (block: any) => block?.text ?? block?.content ?? '';

const getThinkingFromBlock = (block: any) => block?.thinking ?? block?.content ?? '';

const getToolNameFromBlock = (block: any) => block?.name ?? block?.toolName ?? '';

const shouldDisplayToolName = (toolName?: string) => Boolean(toolName && toolName !== '__fragment__');

const isTextBlock = (block: any) => {
  const type = getBlockType(block);
  return type === 'text' || type === 'textblock';
};

const isThinkingBlock = (block: any) => {
  const type = getBlockType(block);
  return type === 'thinking' || type === 'thinkingblock';
};

const isToolUseBlock = (block: any) => {
  const type = getBlockType(block);
  return type === 'tool_use';
};

const isToolResultBlock = (block: any) => {
  const type = getBlockType(block);
  return type === 'tool_result' || type === 'toolresultblock';
};

const createNavigationBlockId = (data: any) => {
  const content = JSON.stringify(data ?? null);
  let hash = 0;

  for (let index = 0; index < content.length; index += 1) {
    hash = (hash * 31 + content.charCodeAt(index)) | 0;
  }

  return `navigation-${Math.abs(hash).toString(36)}`;
};

const normalizeStructuredOutput = (data: any): StructuredOutput | null => {
  if (!data || typeof data !== 'object' || !data.contentType) {
    return null;
  }

  const contentType = String(data.contentType).toUpperCase() as ContentType;
  const blockId = data.blockId || (contentType === ContentType.NAVIGATION ? createNavigationBlockId(data.data) : '');
  if (!blockId) return null;

  return {
    ...data,
    blockId,
    contentType,
  };
};

const tryCacheStructuredOutput = (value: any, cache: Map<string, StructuredOutput>) => {
  // 1. 先尝试直接作为对象
  const output = normalizeStructuredOutput(value);
  if (output) {
    cache.set(output.blockId, output);
    return;
  }

  // 2. 如果不是字符串，跳过
  if (typeof value !== 'string') {
    return;
  }

  try {
    // 3. 先尝试单层 parse
    let parsed = JSON.parse(value);

    // 4. 如果 parse 结果还是字符串，再 parse 一次（双层 JSON 情况）
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }

    // 5. 处理数组或单个对象
    if (Array.isArray(parsed)) {
      parsed.forEach(item => tryCacheStructuredOutput(item, cache));
      return;
    }

    const parsedOutput = normalizeStructuredOutput(parsed);
    if (parsedOutput) {
      cache.set(parsedOutput.blockId, parsedOutput);
    }
  } catch (error) {
    // 普通工具文本不展示也不缓存
  }
};

export const cacheToolResultOutputs = (event: any, cache: Map<string, StructuredOutput>) => {
  const payload = getEventPayload(event);

  if (Array.isArray(payload?.newOutputs)) {
    payload.newOutputs.forEach((output: any) => tryCacheStructuredOutput(output, cache));
  }

  getMessageContent(event).forEach(block => {
    if (!isToolResultBlock(block)) {
      return;
    }

    const outputs = Array.isArray(block.output) ? block.output : [];
    if (!Array.isArray(block.output)) {
      tryCacheStructuredOutput(block.output, cache);
    }

    outputs.forEach(outputBlock => {
      if (isTextBlock(outputBlock)) {
        tryCacheStructuredOutput(getTextFromBlock(outputBlock), cache);
      } else {
        tryCacheStructuredOutput(outputBlock, cache);
      }
    });
  });
};

export const collectToolResultStatus = (event: any) => {
  const toolNames = getMessageContent(event).filter(isToolResultBlock).map(getToolNameFromBlock).filter(Boolean);
  const displayToolNames = toolNames.filter(shouldDisplayToolName);
  if (!displayToolNames.length) {
    return '';
  }

  return `已返回 ${displayToolNames.join('、')}`;
};

export const collectReasoningContent = (event: any): ReasoningContent => {
  let text = '';
  let thinking = '';
  let toolStatus = '';
  let hasToolUse = false;
  let hasToolUseBlock = false;

  for (const block of getMessageContent(event)) {
    if (isTextBlock(block)) {
      text += getTextFromBlock(block);
    } else if (isThinkingBlock(block)) {
      thinking += getThinkingFromBlock(block);
    } else if (isToolUseBlock(block)) {
      hasToolUseBlock = true;
      const toolName = getToolNameFromBlock(block);
      if (shouldDisplayToolName(toolName)) {
        hasToolUse = true;
        toolStatus = `调用工具 ${toolName}`;
      }
    }
  }

  return { text, thinking, toolStatus, hasToolUse, hasToolUseBlock };
};

export const getReasoningEventState = (event: any, reasoningContent: ReasoningContent): ReasoningEventState => {
  const isLast = getEventLast(event);
  return {
    isLast,
    isFinalAnswer: isLast && !reasoningContent.hasToolUseBlock,
  };
};

// 提取思考时间（从 metadata._chat_usage.time）
export const getThinkingTime = (event: any): number | undefined => {
  const payload = getEventPayload(event);
  const message = getMessage(event);
  const metadata = message?.metadata ?? payload?.message?.metadata;
  const chatUsage = metadata?.['_chat_usage'];
  if (chatUsage?.time && typeof chatUsage.time === 'number') {
    // time 单位是秒，直接返回
    return Math.round(chatUsage.time);
  }
  return undefined;
};

const getReasoningRound = (streamState: StreamState, messageId: string) => {
  let round = streamState.rounds.find(item => item.messageId === messageId);
  if (!round) {
    round = {
      messageId,
      textContent: '',
      thinkingContent: '',
      statusContent: '',
      toolStatuses: [],
      thinkingTime: undefined,
    };
    streamState.rounds.push(round);
  }
  streamState.currentRoundId = messageId;
  return round;
};

const syncLegacyContentFields = (streamState: StreamState) => {
  streamState.currentContent = streamState.rounds.map(round => round.textContent).join('');
  streamState.thinkingContent = streamState.rounds
    .map(round => round.thinkingContent)
    .filter(Boolean)
    .join('\n');
  streamState.statusContent =
    streamState.rounds[streamState.rounds.length - 1]?.statusContent ?? streamState.statusContent;
};

const appendRoundToolStatus = (round: ReasoningRound, statusContent: string) => {
  if (statusContent && round.toolStatuses[round.toolStatuses.length - 1] !== statusContent) {
    round.toolStatuses.push(statusContent);
  }
};

export const applyReasoningContentToRound = (
  streamState: StreamState,
  messageId: string,
  reasoningContent: ReasoningContent,
  eventState: ReasoningEventState,
  thinkingTime?: number,
) => {
  const round = getReasoningRound(streamState, messageId);
  const hadTextBefore = Boolean(streamState.currentContent);
  round.textContent =
    eventState.isFinalAnswer && reasoningContent.text
      ? reasoningContent.text
      : mergeReasoningContent(round.textContent, reasoningContent.text);
  round.thinkingContent =
    eventState.isFinalAnswer && reasoningContent.thinking
      ? reasoningContent.thinking
      : mergeReasoningContent(round.thinkingContent, reasoningContent.thinking);
  // 设置思考时间（不累计，每次都是独立的时间）
  if (thinkingTime !== undefined) {
    round.thinkingTime = thinkingTime;
  }
  round.statusContent = reasoningContent.hasToolUse
    ? reasoningContent.toolStatus || '正在调用工具...'
    : eventState.isLast
      ? ''
      : round.statusContent;
  if (reasoningContent.hasToolUse) {
    appendRoundToolStatus(round, round.statusContent);
  }

  syncLegacyContentFields(streamState);

  return {
    isFirstText: Boolean(reasoningContent.text && !hadTextBefore),
  };
};

export const updateCurrentRoundStatus = (streamState: StreamState, statusContent: string) => {
  const currentRound = streamState.rounds.find(round => round.messageId === streamState.currentRoundId);
  if (currentRound) {
    currentRound.statusContent = statusContent;
    appendRoundToolStatus(currentRound, statusContent);
  }
  streamState.statusContent = statusContent;
};

export const clearRoundStatuses = (streamState: StreamState) => {
  streamState.rounds.forEach(round => {
    if (round.statusContent === '处理返回结果...') {
      round.statusContent = '';
    }
    round.toolStatuses = round.toolStatuses.filter(status => status !== '处理返回结果...');
  });
  if (streamState.statusContent === '处理返回结果...') {
    streamState.statusContent = '';
  }
};

export const buildAssistantParts = (
  streamState: Pick<
    StreamState,
    | 'structuredOutputsCache'
    | 'currentContent'
    | 'thinkingContent'
    | 'statusContent'
    | 'currentRoundId'
    | 'replyFinished'
    | 'rounds'
  >,
): MessagePart[] => {
  const parts: MessagePart[] = [];
  const processVisibleRenderMarkers = (text: string) => processRenderMarkers(text, streamState.structuredOutputsCache);
  const markPartsReplying = (messageParts: MessagePart[], replying: boolean) =>
    messageParts.map(part => (part.type === 'text' ? { ...part, replying } : part));
  if (streamState.rounds.length) {
    let timelineRounds: ThinkingTimelineRound[] = [];
    const flushTimeline = () => {
      if (!timelineRounds.length) return;
      const timelineReplying = timelineRounds.some(round => round.active);
      parts.push({
        type: 'thinkingTimeline',
        data: timelineRounds,
        replying: timelineReplying,
      });
      timelineRounds = [];
    };

    streamState.rounds.forEach(round => {
      const isCurrentRound = !streamState.replyFinished && round.messageId === streamState.currentRoundId;
      const hasThinking = Boolean(round.thinkingContent || round.toolStatuses.length);
      const renderedTextParts = processVisibleRenderMarkers(round.textContent);
      const hasTextContent = renderedTextParts.length > 0;

      if (hasThinking) {
        const textTitle = round.textContent
          .replace(/[#*_`>\n]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        const roundNumber = timelineRounds.length + 1;

        timelineRounds.push({
          id: round.messageId,
          // title:
          //   textTitle && round.toolStatuses.length
          //     ? textTitle.length > 28
          //       ? `${textTitle.slice(0, 28)}...`
          //       : textTitle
          //     : `第${roundNumber}轮思考`,
          title: `第${roundNumber}轮思考`,
          content: round.thinkingContent || '正在思考...',
          toolStatuses: round.toolStatuses,
          thinkingTime: round.thinkingTime,
          active: isCurrentRound && !hasTextContent,
        });
      }

      if (hasTextContent) {
        // 有实际可渲染内容时才作为思考分段边界。
        flushTimeline();
        parts.push(...markPartsReplying(renderedTextParts, isCurrentRound));
      }
    });

    flushTimeline();
  } else if (streamState.thinkingContent) {
    const hasTextContent = Boolean(streamState.currentContent);
    parts.push({
      type: 'thinkingBlock',
      data: streamState.thinkingContent,
      replying: !streamState.replyFinished && !hasTextContent,
    });
    parts.push(
      ...markPartsReplying(
        processVisibleRenderMarkers(streamState.currentContent),
        !streamState.replyFinished,
      ),
    );
    if (streamState.statusContent) {
      parts.push({ type: 'toolStatus', data: streamState.statusContent });
    }
  }

  if (parts.length === 0 && !streamState.replyFinished) {
    parts.push({ type: 'thinkingBlock', data: '正在思考...', replying: !streamState.replyFinished });
  }

  return parts;
};
